
import React, { useEffect, useState, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import apiPath from "../../../utils/pathObj";
import { apiGet, apiPost } from "../../../utils/apiFetch";
import GridLayout from "react-grid-layout";
import useToastContext from 'hooks/useToastContext'
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import AuthContext from 'context/AuthContext'
import helpers from "utils/helpers";

const HiringView = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const notification = useToastContext();
  const { id } = location?.state || {};
  const [formData, setFormData] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [layouts, setLayouts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useContext(AuthContext);
  console.log(user._id, "user");

  useEffect(() => {
    if (id) {
      const savedLayouts = sessionStorage.getItem(`layouts-${id}`);
      if (savedLayouts) {
        setLayouts(JSON.parse(savedLayouts));
      }
      fetchFormData();
    } else {
      navigate("/suggested_forms");
    }
  }, [id]);

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  console.log(containerWidth, "container width");

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!containerRef.current) return;

      const observer = new ResizeObserver(([entry]) => {
        setContainerWidth(entry.contentRect.width);
      });

      observer.observe(containerRef.current);
      
      return () => {
        if (containerRef.current) {
          observer.unobserve(containerRef.current);
        }
        observer.disconnect();
      };
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // console.log(layouts, "layouts");

  const fetchFormData = async () => {
    try {
      setIsLoading(true); // Start loading
      const res = await apiGet(`${apiPath.getFormsById}/${id}`);
      const form = res?.data?.results;

      const values = {};
      const layoutByGroup = {};

      form?.customFields?.forEach((group, groupIndex) => {
        layoutByGroup[groupIndex] = group.map((field, fieldIndex) => {
          // If field has existing layout, use that
          if (field.layout) {
            return {
              i: `${groupIndex}-${fieldIndex}`,
              x: field.layout.x,
              y: field.layout.y,
              w: field.layout.w,
              h: field.layout.h,
            };
          }

          // Default layout based on field type
          let defaultWidth = 4;
          let defaultHeight = 1;

          if (field.type === "heading") {
            defaultWidth = 12;
            defaultHeight = 2;
          } else if (field.type === "textarea") {
            defaultWidth = 12;
            defaultHeight = 4;
          } else if (field.type === "checkbox" || field.type === "radio") {
            defaultWidth = 6;
          }

          return {
            i: `${groupIndex}-${fieldIndex}`,
            x: (fieldIndex % 2) * 4, // Two columns by default
            y: Infinity, // Let it auto-position vertically
            w: defaultWidth,
            h: defaultHeight,
            static: field.type === "heading",
          };
        });

        group.forEach((field, fieldIndex) => {
          const fieldId = field?.id ?? `${groupIndex}-${fieldIndex}`;
          values[fieldId] =
            field?.value || (field?.type === "checkbox" ? [] : "");
        });
      });

      // Check if we have saved layouts in sessionStorage
      const savedLayouts = sessionStorage.getItem(`layouts-${id}`);

      // Update the formData with layouts
      const updatedFormData = { ...form };
      updatedFormData.customFields = form.customFields.map(
        (group, groupIndex) =>
          group.map((field, fieldIndex) => ({
            ...field,
            layout: savedLayouts
              ? JSON.parse(savedLayouts)[groupIndex]?.[fieldIndex] || {
                  x: layoutByGroup[groupIndex][fieldIndex].x,
                  y: layoutByGroup[groupIndex][fieldIndex].y,
                  w: layoutByGroup[groupIndex][fieldIndex].w,
                  h: layoutByGroup[groupIndex][fieldIndex].h,
                }
              : {
                  x: layoutByGroup[groupIndex][fieldIndex].x,
                  y: layoutByGroup[groupIndex][fieldIndex].y,
                  w: layoutByGroup[groupIndex][fieldIndex].w,
                  h: layoutByGroup[groupIndex][fieldIndex].h,
                },
          }))
      );

      // Set all states together to prevent inconsistent renders
      setFormData(updatedFormData);
      setFieldValues(values);
      setLayouts(savedLayouts ? JSON.parse(savedLayouts) : layoutByGroup);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false); // End loading
    }
  };

  console.log(formData, "formData");

  const handleChange = (e, fieldId, type) => {
    const { value, checked, files } = e.target;

    setFieldValues((prev) => {
      if (type === "checkbox") {
        const current = prev[fieldId] || [];
        return {
          ...prev,
          [fieldId]: checked
            ? [...current, value]
            : current.filter((v) => v !== value),
        };
      }

      if (type === "file") {
        return { ...prev, [fieldId]: files[0] };
      }

      return { ...prev, [fieldId]: value };
    });
  };

  const handleAddToFormsManager = async () => {

    if (!formData || !formData._id) return;

    const updatedFormData = { ...formData };
    console.log("----Adding form to Forms Manager----");

    const payload = {
      formName: updatedFormData.formName,
      formType: updatedFormData.formType,
      category: updatedFormData.category,
      createdBy: user?._id,
      customFields: JSON.stringify(updatedFormData.customFields),
      status: "active",
      isDuplicate: true,
      duplicateId: updatedFormData._id,
    };
    console.log("Payload to be sent:", payload);

    try {
      const res = await apiPost(apiPath.addSuggestedForm, payload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (res?.data?.success) {
        notification.success(res?.data?.message || "Form Added successfully!");
        navigate("/forms_manager");
      } else {
        notification.error(res?.data?.results || "Failed to add form");
      }
    } catch (error) {
      console.error("Error Adding form:", error);
      notification.error(error?.res?.data?.message || 'Add failed');
    }
  };

  const renderField = (field, groupIndex, fieldIndex) => {
    const fieldId = field?.id ?? `${groupIndex}-${fieldIndex}`;
    const value = fieldValues[fieldId] ?? "";
    const { type, label, options, placeholder, mandatory, styles, boldText} = field;

    const labelStyle = "min-w-max text-xs whitespace-nowrap mr-[5px]";
    const inputStyle = "flex-1 border px-3 py-2 bg-gray-200 text-xs";
    const baseLabelStyle = "text-sm mr-2";
    const boldStyle = boldText ? "font-bold" : "font-normal";
    const requiredIndicator = mandatory ? <span className="text-red-500 ml-1">*</span> : null;

    console.log(mandatory, "mandatory");

    switch (type) {
      case "text":
        return (
          <div className={`flex items-center ${styles?.container || ""}`} key={fieldId}>
            <label className={`${baseLabelStyle} ${boldStyle}`}>
              {label}
              {requiredIndicator}
            </label>
          </div>
        );
      
        case "heading": {
        let headingText, subheadingText;

        if (typeof value === "string") {
          [headingText, subheadingText] = (value || "")
            .split(",")
            .map((s) => s.trim());
        } else if (typeof value === "object" && value !== null) {
          headingText = value.heading || "";
          subheadingText = value.subheading || "";
        } else {
          headingText = "";
          subheadingText = "";
        }

        return (
          <div
            key={fieldId}
            className="mb-4 p-4 bg-[#E1E1E1] w-full rounded text-center text-gray-700"
          >
            <h2 className="text-lg font-semibold mb-2">{headingText}</h2>
            {subheadingText && <p className="text-lg">{subheadingText}</p>}
          </div>
        );
      }
      case "input":
      case "select":
      case "date":
        return (
          <div className="mb-4 flex items-center gap-1" key={fieldId}>
            <label className={`text-xs ${labelStyle}`}>
              {label}{" "}
              {mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            {type === "input" && (
              <input
                type="text"
                className={inputStyle}
                value={value}
                onChange={(e) => handleChange(e, fieldId, type)}
                placeholder={placeholder}
              />
            )}
            {type === "date" && (
              <input
                type="date"
                className={`${inputStyle || ''} non-draggable`}
                value={value}
                onChange={(e) => handleChange(e, fieldId, type)}
              />
            )}
            {type === "select" && (
              <select
                className={inputStyle}
                value={value}
                onChange={(e) => handleChange(e, fieldId, type)}
              >
                <option value="">Select</option>
                {options?.map((opt, idx) => (
                  <option key={idx} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            )}
          </div>
        );
      case "textarea":
        return (
          <div className="mb-4" key={fieldId} style={{ clear: "both" }}>
            <div
              className="whitespace-pre-wrap  px-3 py-2 mb-10 rounded "
              style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}
            >
              {value || "No content"}
            </div>
          </div>
        );
      case "textEditor":
        return (
          <div className="mb-4" key={fieldId} style={{ clear: "both" }}>
            <label className={`text-sm ${labelStyle}`}>
              {mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div
              className="bg-white form-view inner-html"
              // ✅ TinyMCE content can be directly rendered
              dangerouslySetInnerHTML={{ __html: value }}
            />
          </div>
        );
  case "radio":
        return (
          <div className={styles.optionsContainer} key={fieldId}>
            <label className={`${baseLabelStyle}`}>
              {label}{" "}
              {mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className=" non-draggable flex gap-4 flex-wrap">
              {options?.map((opt, idx) => (
                <label key={idx} className="non-draggable flex items-center gap-2 ">
                  <input
                    type="radio"
                    name={fieldId}
                    className="non-draggable"
                    value={opt}
                    checked={value === opt}
                    onChange={(e) => handleChange(e, fieldId, type)}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
        );
      case "checkbox":
        return (
          <div className={styles.optionsContainer} key={fieldId}>
            <label className={`${baseLabelStyle}`}>
              {label}{" "}
              {mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="flex gap-4 flex-wrap">
              {options?.map((opt, idx) => (
                <label key={idx} className="non-draggable flex items-center gap-2">
                  <input
                    type="checkbox"
                    name={`${fieldId}-${opt}`}
                    value={opt}
                    checked={(value || []).includes(opt)}
                    onChange={(e) => handleChange(e, fieldId, type)}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
        );
      case "file":
        return (
          <div className="mb-4" key={fieldId}>
            <label className={`${baseLabelStyle}`}>
              {label}{" "}
              {mandatory && <span className="text-red-500 ml-1">*</span>}
            </label>
            {value ? (
              <img
                src={value}
                alt="Uploaded image"
                className="max-w-xs h-auto rounded border"
              />
            ) : (
              <p className="text-gray-500 text-sm">No file uploaded</p>
            )}
          </div>
        );
      case "html":
        return (
          <div
            key={fieldId}
            className="mb-4 inner-html"
            dangerouslySetInnerHTML={{ __html: label }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-[#F9F9F9]">
      <div className="py-4 px-3">
        <div className="bg-white border border-[#E9EDF9] rounded-lg">
          <div className="p-6 w-full" ref={containerRef}>
            {formData?.customFields?.map((group, groupIndex) => (
              <GridLayout
                key={groupIndex}
                className="layout mb-8"
                cols={12}
                rowHeight={20} // Optionally reduce height too
                margin={[8, 4]}
                style={{ fontSize: "12px" }}
                width={containerWidth}
                layout={layouts[groupIndex]}
                draggableCancel=".non-draggable, .ql-toolbar, .ql-container"
                
              >
                {group.map((field, fieldIndex) => (
                  <div
                    key={`${groupIndex}-${fieldIndex}`}
                    data-grid={
                      layouts[groupIndex]?.[fieldIndex] ?? {
                        i: `${groupIndex}-${fieldIndex}`,
                        x: 0,
                        y: 0,
                        w: 4,
                        h: 1,
                      }
                    }
                  >
                    {renderField(field, groupIndex, fieldIndex)}
                  </div>
                ))}
              </GridLayout>
            ))}
            <div className="border-t pt-4 flex justify-center">
              <button
                onClick={() => navigate("/suggested_forms")}
                className="bg-[#E1E1E1] px-12 py-2.5 rounded text-sm text-black"
              >
                {t("O_BACK")}
              </button>
              <button
                  onClick={() => {
                        helpers.alertFunction(
                          `Are you sure you want to add '${formData.formName}' to Forms Manager?`,
                          formData,
                          handleAddToFormsManager,
                          false
                        );
                      }}
                className="ml-4 bg-[#007BFF] text-white px-8 py-2.5 rounded text-sm flex justify-center"
              >
                {t("ADD_TO_FM")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HiringView;
